/**
 * Client-side search matching for autocomplete free options (org-custom entries).
 */

export function parseSearchTokens(query: string): string[] {
  return query
    .trim()
    .split(/[ ,]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Pattern matching `token` only at the start of a word, mirroring how the
 * patient service matches so the two never disagree about what a query hit.
 * The boundary is skipped for tokens opening with a non-word character, which
 * could never be preceded by one.
 */
export function wordPrefixPattern(token: string): string {
  const escaped = escapeRegex(token);
  return /^\w/.test(token) ? `\\b${escaped}` : escaped;
}

function tokenMatchesLabel(label: string, token: string): boolean {
  const lowerLabel = label.toLowerCase();
  const lowerToken = token.toLowerCase();

  if (lowerLabel.startsWith(lowerToken)) return true;

  const wordBoundary = new RegExp(`\\b${escapeRegex(lowerToken)}\\b`, "i");
  if (wordBoundary.test(label)) return true;

  return lowerLabel.includes(lowerToken);
}

export function matchesSearchQuery(
  label: string,
  searchQuery: string,
): boolean {
  const tokens = parseSearchTokens(searchQuery);
  if (tokens.length === 0 || !label) return false;

  return tokens.every((token) => tokenMatchesLabel(label, token));
}

function codesEqual(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

const FREE_OPTION_SEARCH_FIELDS = [
  "snomedFSN",
  "display",
  "text",
  "value",
] as const;

export function getFreeOptionSearchTexts(
  option: Record<string, unknown>,
  getLabel?: (option: Record<string, unknown>) => string,
): string[] {
  const texts = new Set<string>();

  if (getLabel) {
    const label = getLabel(option);
    if (label?.trim()) texts.add(label.trim());
  }

  for (const key of FREE_OPTION_SEARCH_FIELDS) {
    const value = option[key];
    if (typeof value === "string" && value.trim()) {
      texts.add(value.trim());
    }
  }

  return Array.from(texts);
}

export function optionMatchesSearchQuery(
  option: Record<string, unknown>,
  searchQuery: string,
  getLabel?: (option: Record<string, unknown>) => string,
): boolean {
  const texts = getFreeOptionSearchTexts(option, getLabel);
  return texts.some((text) => matchesSearchQuery(text, searchQuery));
}

/**
 * True when the option is the same entity as a currently selected value.
 * Matches by org free-option id or code/snomedCID only — not display text alone.
 */
export function isOptionInCurrentValue(
  option: Record<string, unknown>,
  value: unknown,
  multiple?: boolean,
): boolean {
  if (value == null) return false;

  const values = multiple ? (Array.isArray(value) ? value : []) : [value];

  return values.some((selected) => {
    if (!selected || typeof selected !== "object") return false;
    const sel = selected as Record<string, unknown>;

    if (option.id && sel.id && option.id === sel.id) return true;

    const optionCode =
      option.code ??
      option.snomedCID ??
      option.medicationCode ??
      option.identifier;
    const selectedIdentifier =
      sel.identifier != null && typeof sel.identifier === "object"
        ? (sel.identifier as { value?: unknown }).value
        : sel.identifier;

    const selectedCode =
      sel.code ??
      sel.snomedCID ??
      sel.medicationCode ??
      selectedIdentifier ??
      (sel.coding as { code?: unknown }[] | undefined)?.[0]?.code;

    return codesEqual(optionCode, selectedCode);
  });
}

function hasCommittedSelection(value: unknown, multiple?: boolean): boolean {
  if (value == null) return false;
  if (multiple) {
    return Array.isArray(value) && value.length > 0;
  }
  return true;
}

export function filterRemoteFreeOptions<T>({
  freeOptions,
  searchQuery,
  value,
  multiple,
  getLabel,
}: {
  freeOptions: T[];
  searchQuery: string;
  value: unknown;
  multiple?: boolean;
  getLabel?: (option: Record<string, unknown>) => string;
}): T[] {
  const trimmedQuery = searchQuery.trim();

  if (!trimmedQuery) {
    if (!hasCommittedSelection(value, multiple)) {
      return freeOptions;
    }

    return freeOptions.filter((option) =>
      isOptionInCurrentValue(
        option as Record<string, unknown>,
        value,
        multiple,
      ),
    );
  }

  return freeOptions.filter((option) =>
    optionMatchesSearchQuery(
      option as Record<string, unknown>,
      trimmedQuery,
      getLabel,
    ),
  );
}
